import { Loader2, Pencil } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";

export interface InlineEditFieldOption {
  value: string;
  label: string;
}

export interface InlineEditFieldProps {
  label: string;
  value: string | null | undefined;
  fieldName: string;
  type?: "text" | "date" | "select";
  options?: InlineEditFieldOption[];
  disabled?: boolean;
  displayValue?: string;
  onSave: (fieldName: string, value: string) => Promise<void> | void;
  className?: string;
}

export function InlineEditField({
  label,
  value,
  fieldName,
  type = "text",
  options,
  disabled = false,
  displayValue,
  onSave,
  className,
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Dismiss select editing on click outside
  useEffect(() => {
    if (!isEditing || type !== "select") return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        // Check if click is inside a popover (radix portal)
        const target = e.target as HTMLElement;
        if (target.closest("[data-radix-popper-content-wrapper]")) return;
        setIsEditing(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing, type]);

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  const handleSave = useCallback(
    async (newValue: string) => {
      if (newValue === (value || "")) {
        setIsEditing(false);
        return;
      }
      setIsSaving(true);
      try {
        await onSave(fieldName, newValue);
        setIsEditing(false);
      } catch {
        // error handled by parent
      } finally {
        setIsSaving(false);
      }
    },
    [value, fieldName, onSave],
  );

  const handleCancel = useCallback(() => {
    setEditValue(value || "");
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave(editValue);
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [editValue, handleSave, handleCancel],
  );

  const handleBlur = useCallback(() => {
    if (!isSaving) {
      handleSave(editValue);
    }
  }, [editValue, handleSave, isSaving]);

  const handleSelectChange = useCallback(
    (newValue: string) => {
      setEditValue(newValue);
      handleSave(newValue);
    },
    [handleSave],
  );

  const startEditing = useCallback(() => {
    if (!disabled) {
      setEditValue(value || "");
      setIsEditing(true);
    }
  }, [disabled, value]);

  const shownValue = displayValue || value || "-";

  if (isEditing) {
    return (
      <div
        ref={containerRef}
        className={cn("px-3 py-3 min-h-[60px] flex flex-col justify-center", className)}
      >
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
          {label}
        </span>
        {type === "select" && options ? (
          <SearchableSelect
            value={editValue}
            onValueChange={handleSelectChange}
            options={options}
            placeholder="Selecione..."
            triggerClassName="h-[22px] min-h-0 text-sm font-medium border-0 shadow-none bg-transparent px-0 rounded-none focus:ring-0"
          />
        ) : (
          <Input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="h-[22px] text-sm font-medium border-0 shadow-none bg-transparent px-0 rounded-none focus-visible:ring-0"
            disabled={isSaving}
          />
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        "group px-3 py-3 min-h-[60px] flex flex-col justify-center transition-colors cursor-pointer text-left w-full",
        !disabled && "hover:bg-muted/50",
        disabled && "cursor-default",
        className,
      )}
      onClick={!disabled ? startEditing : undefined}
      tabIndex={!disabled ? 0 : -1}
      disabled={disabled}
    >
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </span>
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "text-sm font-medium truncate",
            (!value || value === "") && "text-muted-foreground",
          )}
        >
          {isSaving ? (
            <span className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Salvando...
            </span>
          ) : (
            shownValue
          )}
        </span>
        {!disabled && (
          <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        )}
      </div>
    </button>
  );
}
